import React from 'react';
import PropTypes from 'prop-types';
import fetch from 'isomorphic-fetch';
import get from 'lodash.get';
import getConfig from 'next/config';

import BTE from 'lib/BTE';
import InactivityMonitor from 'lib/InactivityMonitor';
import $t from 'lib/trackingService';
import {
  getAdLevelForPID,
  getPlaymakerConfiguration,
} from 'lib/playmaker';
import {
  VIDEO_PLAYING_GLOBAL_EVENT,
  VIDEO_PLAYING_EVENT_ATTENUATION,
} from 'lib/videoUtils';

import { WithSticky } from 'components/WithSticky';

const app = 'playmaker';
const { publicRuntimeConfig } = getConfig();

$t('register', 'playMaker_init');

class Playmaker extends React.Component {
  static propTypes = {
    // enable ad logic debug logs
    adebug: PropTypes.oneOf([0, 1]),
    APResource: PropTypes.string,
    authorization: PropTypes.string,
    autoplay: PropTypes.bool,
    autoplayMuted: PropTypes.bool,
    // Stop initialization of player until user clicks to activate/play
    clickToPlay: PropTypes.oneOf([0, 1]),
    enableComscore: PropTypes.oneOf([0, 1]),
    enableHeartbeat: PropTypes.oneOf([0, 1]),
    enableOmniture: PropTypes.oneOf([0, 1]),
    externalControlbar: PropTypes.oneOf([0, 1]),
    // Hide the overlay text label on video displays
    hideVidLabel: PropTypes.oneOf([0, 1]),
    // if 1 then the stream is considered LIVE only no DVR access
    isLive: PropTypes.bool,
    // 0 for debug, 1 for logs and info, 2 for warnings, 3 for error, 4 for nothing
    logLevel: PropTypes.oneOf([0, 1, 2, 3, 4]),
    onVideoComplete: PropTypes.func,
    pid: PropTypes.number.isRequired,
    // Freewheel hash ID from MVPD service
    providerId: PropTypes.string,
    stickyClosed: PropTypes.bool,
    stickyShouldStick: PropTypes.func,
  };

  static defaultProps = {
    adebug: 0,
    APResource: null,
    authorization: null,
    autoplay: true,
    autoplayMuted: true,
    clickToPlay: 0,
    enableComscore: 0,
    enableHeartbeat: 0,
    enableOmniture: 0,
    externalControlbar: 0,
    hideVidLabel: 1,
    isLive: 1,
    logLevel: 4,
    onVideoComplete: Function.prototype,
    providerId: null,
    stickyClosed: false,
    stickyShouldStick: Function.prototype,
  };

  constructor(props) {
    super(props);
    this.state = {
      playing: false,
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.handleFrameMessages);
    this.sendPlayerConfig();

    // Listen for global video playing event
    BTE.on(
      VIDEO_PLAYING_GLOBAL_EVENT,
      this.onEventGlobalVideoPlaying,
      VIDEO_PLAYING_EVENT_ATTENUATION,
    );
  }

  componentDidUpdate(prevProps) {
    const { stickyClosed: wasClosed } = prevProps;
    const { stickyClosed: isClosed } = this.props;

    if (!wasClosed && isClosed) {
      // Pause player
      this.sendMessage('PLAYER_EVENT', { event: 'PAUSE' });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleFrameMessages);

    // Remove global video playing event listener
    BTE.remove(
      VIDEO_PLAYING_GLOBAL_EVENT,
      this.onEventGlobalVideoPlaying,
      VIDEO_PLAYING_EVENT_ATTENUATION,
    );
  }

  sendPlayerConfig = async () => {
    const {
      adebug,
      APResource,
      authorization,
      autoplay,
      autoplayMuted,
      clickToPlay,
      enableComscore,
      enableHeartbeat,
      enableOmniture,
      externalControlbar,
      hideVidLabel,
      isLive,
      logLevel,
      pid,
      providerId,
    } = this.props;

    const playerVars = {
      ...getPlaymakerConfiguration(pid),
      adebug,
      adLevel: getAdLevelForPID(pid, publicRuntimeConfig),
      autoplayMuted: autoplay && autoplayMuted ? 1 : 0,
      clickToPlay,
      enableComscore,
      enableHeartbeat,
      enableOmniture,
      externalControlbar,
      hideVidLabel,
      'hlsjs.autoStartLoad': true,
      isLive: isLive ? 1 : 0,
      logLevel,
      // Player bug with unmuting from autoplayMuted, volume bar is hidden
      muted: autoplay && autoplayMuted ? 1 : 0,
      mutedMobile: autoplay && autoplayMuted ? 1 : 0,
      pid,
      showPlay: 1,
      statusService: 1,
    };
    if (providerId) {
      playerVars.providerId = providerId;
    }
    if (authorization) {
      playerVars.token = window.btoa(authorization); // base64 encoded
    }
    if (APResource) {
      playerVars.APResource = encodeURIComponent(APResource);
    }

    const playerConfig = await fetch(`https://stream.nbcsports.com/data/event_config_${pid}.json`)
      .then((response) => response.json());

    this.sendMessage('OPTIONS', { playerConfig, playerVars });
  }

  sendMessage = (type, data) => {
    const { contentWindow } = this.frameRef;
    if (contentWindow) {
      contentWindow.postMessage({
        app,
        type,
        data,
      }, '*');
    }
  }

  onEventGlobalVideoPlaying = ({ ref }) => {
    const { playing } = this.state;
    if (playing && ref && typeof ref.isEqualNode === 'function' && !ref.isEqualNode(this.frameRef)) {
      this.sendMessage('PLAYER_EVENT', { event: 'PAUSE' });
    }
  }

  onVideoPlaying = () => {
    this.setState({ playing: true });
    // Pause timer
    InactivityMonitor.stopMonitoring();
    // Enable sticky wrapper
    this.enableSticky();
    // Trigger global video playing event
    BTE.trigger(
      VIDEO_PLAYING_GLOBAL_EVENT,
      { ref: this.frameRef },
      VIDEO_PLAYING_EVENT_ATTENUATION,
    );
  }

  onVideoPaused = () => {
    this.setState({ playing: false });

    // Resume timer
    InactivityMonitor.startMonitoring();
  }

  onVideoComplete = (data) => {
    const { onVideoComplete } = this.props;
    if (typeof onVideoComplete === 'function') {
      onVideoComplete(data);
    }
  }

  handleError = (err) => {
    if (typeof window !== 'undefined' && window.console) {
      window.console.log(err);
    }
  }

  handleFrameMessages = (e) => {
    const { contentWindow } = this.frameRef;
    if (!contentWindow) {
      return;
    }

    const messageApp = get(e, 'data.app', false);
    if (e.isTrusted && e.data && messageApp === app) {
      const type = get(e, 'data.type', null);
      const data = get(e, 'data.data', {});

      switch (type) {
        case 'READY':
          this.sendPlayerConfig();
          break;

        case 'rehtml5::playerready':
          // Initialize tracking remotely
          $t('track', 'playMaker_init', { iframeObj: contentWindow });
          break;

        case 'html5Notification':
          this.handleFrameNotification(data);
          break;

        case 'scteMessageStarted':
          // Ignore `cotIndicated` and `contentIdentification`
          // if (data.segmentationTypeId && data.segmentationTypeId > 1) {
          //   console.log(scteMessage);
          // }
          break;

        default:
          // console.log(type, data);
      }
    }
  }

  handleFrameNotification = (notification) => {
    const { type, data: jsonData } = notification;
    const data = JSON.parse(jsonData) || {};

    switch (type) {
      case 'CONTROLBAR_VISIBLE':
      case 'UPDATE_TIME':
        // ignore
        break;

      case 'MEDIA_STATE_CHANGE':
        switch (data.status) {
          case 'rehtml5player:playing':
            this.onVideoPlaying(data);
            break;

          case 'rehtml5player:paused':
            this.onVideoPaused(data);
            break;

          case 'rehtml5player:complete':
            this.onVideoComplete(data);
            break;

          case 'rehtml5player:seeking':
          case 'rehtml5player:initialized':
          default:
            // console.log(data);
        }
        break;

      default:
        // console.log(type, data);
        // Observed:
        // AD_(START|COMPLETE), AD_BREAK_(START|COMPLETE)
        // MAIN_MEDIA_CHANGE
        // MEDIA_ERROR
    }
  }

  enableSticky = () => {
    const { stickyShouldStick } = this.props;
    // Enable sticky
    stickyShouldStick();
  }

  render() {
    return (
      <iframe
        title="video player"
        src="/playmaker/player.html"
        width="100%"
        height="100%"
        frameBorder={0}
        scrolling="no"
        allowFullScreen
        ref={(ref) => { this.frameRef = ref; }}
      />
    );
  }
}

export default WithSticky(Playmaker);
